/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable rover/no-platform-specific-globals-or-imports */
import { RouteConfig } from 'react-router-config';

import type { DuckOptions, EffectCreator } from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import { UserInformation } from '@rover/rsdk/src/apiClient/latest';
import { isMobileBrowser } from '@rover/rsdk/src/modules/Network/userAgent';
import type { DataLayerEvent } from '@rover/types';
import { dispatchCrossPageEvents } from '@rover/utilities/fireCrossPageEvent';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';
import shouldLoadAmplitudeSessionReplay from '@rover/utilities/shouldLoadAmplitudeSessionReplay';
import shouldLoadFullStory from '@rover/utilities/shouldLoadFullStory';
import { emitCoreWebVitals } from '@rover/utilities/webVitals';

export type State = {};

type Actions = {};

type Effects = {
  fireDataLayerLoaded: EffectCreator<
    { pageCategory: string; currentUser: UserInformation },
    Promise<void>
  >;
  fireRouteChange: EffectCreator<{ pathname: string }, Promise<void>>;
  fireSessionEvents: EffectCreator<DataLayerEvent[], Promise<void>>;
  fireLoadImpactJs: EffectCreator<{ currentUser: UserInformation }, Promise<void>>;
  fireLoadAmplitudeJs: EffectCreator<void, Promise<void>>;
  fireLoadAmplitudeSessionReplayJs: EffectCreator<(string) => boolean, Promise<void>>;
  fireLoadFullStoryJs: EffectCreator<(string) => boolean, Promise<void>>;
  fireCWVMetricsEvents: EffectCreator<RouteConfig, Promise<void>>;
  fireCrossPageEvents: EffectCreator<void, Promise<void>>;
};

type Selectors = {};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'baseTemplate',
  initialState: {},
  actions: {},
  effects: {
    fireDataLayerLoaded:
      ({ pageCategory = 'uncategorized', currentUser: { opk, providerGroupSlug } }) =>
      async (): Promise<void> => {
        const isAuthed: boolean = !!opk && !!opk.length;
        return fireDataLayerEvent({
          event: 'dataLayer-loaded',
          pageCategory,
          loggedIn: `logged ${isAuthed ? 'in' : 'out'}`,
          ...(isAuthed
            ? {
                personId: opk,
                sitterType: providerGroupSlug,
              }
            : {}),
          deviceType: isMobileBrowser() ? 'mobile' : 'desktop',
        });
      },
    fireRouteChange:
      ({ pathname }) =>
      async (): Promise<void> =>
        fireDataLayerEvent({
          event: 'route-change',
          newRoute: pathname,
        }),
    fireSessionEvents: (events) => async (): Promise<void> => {
      if (events && events.length > 0) {
        events.forEach(fireDataLayerEvent);
      }
    },
    fireLoadAmplitudeJs: () => async (): Promise<void> => {},
    fireLoadImpactJs:
      ({ currentUser: { emailHash = '' } }) =>
      async (): Promise<void> =>
        fireDataLayerEvent({
          event: 'load-impact-js',
          emailSha: emailHash,
        }),
    fireLoadFullStoryJs: (isFlagActive: (string) => boolean) => async (): Promise<void> => {
      if (
        process.env.JS_ENV_CLIENT &&
        shouldLoadFullStory(isFlagActive, window.location.pathname)
      ) {
        fireDataLayerEvent({
          event: 'load-full-story',
        });
      }
    },
    fireLoadAmplitudeSessionReplayJs:
      (isFlagActive: (string) => boolean) => async (): Promise<void> => {
        if (
          process.env.JS_ENV_CLIENT &&
          // Do not enable fullstory and amplitude session replay at the same time
          !shouldLoadFullStory(isFlagActive, window.location.pathname) &&
          shouldLoadAmplitudeSessionReplay(isFlagActive, new URL(window.location.href))
        ) {
          fireDataLayerEvent({
            event: 'load-amplitude-session-replay-js',
          });
        }
      },
    fireCWVMetricsEvents:
      ({ pageCategory, userIsAuthenticated }) =>
      async (): Promise<void> => {
        await emitCoreWebVitals(pageCategory, userIsAuthenticated);
      },
    fireCrossPageEvents: () => async (): Promise<void> => {
      dispatchCrossPageEvents();
    },
  },
  selectors: {},
};

export const { actions, selectors, effects, reducer } = createDuck(options);
